import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../context/AuthContext';

const SubscriptionSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { refetchUser } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const verifySession = async () => {
      if (!sessionId) {
        setError('No session ID found');
        setIsLoading(false);
        return;
      }

      try {
        // Verify the session with your backend
        const apiBase = import.meta.env.VITE_API_BASE || '/api';
        const url = `${apiBase}/stripe/verify-session?session_id=${sessionId}`;
        console.log('Fetching:', url);
        
        const response = await fetch(url);
        console.log('Response status:', response.status, 'OK:', response.ok);
        
        if (response.ok) {
          const data = await response.json();
          console.log('Response data:', data);
          // Refresh user data to get updated subscription
          await refetchUser();
        } else {
          const errorText = await response.text();
          console.error('Response error:', errorText);
          setError('Failed to verify payment');
        }
      } catch (err) {
        console.error('Fetch error:', err);
        setError('An error occurred while verifying your payment');
      } finally {
        setIsLoading(false);
      }
    };

    verifySession();
  }, [sessionId, refetchUser]);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-2xl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-naia-pink mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold text-white mb-2">Processing Payment...</h1>
          <p className="text-gray-400">Please wait while we confirm your subscription.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-2xl">
        <div className="text-center bg-red-500/10 border border-red-500/30 rounded-xl p-8">
          <div className="text-red-400 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-white mb-2">Payment Verification Failed</h1>
          <p className="text-gray-300 mb-6">{error}</p>
          <div className="space-y-4">
            <Link
              to="/subscription"
              className="inline-block px-6 py-3 bg-naia-pink hover:bg-pink-500 text-white rounded-xl font-medium transition-colors"
            >
              Back to Subscription
            </Link>
            <p className="text-gray-400 text-sm">
              If you believe this is an error, please immediately contact support.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12 max-w-2xl">
      <div className="text-center bg-green-500/10 border border-green-500/30 rounded-xl p-8">
        <CheckCircleIcon 
          className="text-green-400 mx-auto mb-4" 
          style={{ fontSize: '4rem' }} 
        />
        <h1 className="text-3xl font-bold text-white mb-2">Welcome to Naia Premium!</h1>
        <p className="text-gray-300 mb-6">
          Your subscription has been successfully activated. You now have access to all premium features.
        </p>

        <div className="bg-gray-800 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-semibold text-white mb-4">What's Next?</h2>
          <div className="space-y-3 text-left">
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="text-green-400 mt-0.5 flex-shrink-0" fontSize="small" />
              <span className="text-gray-300 text-sm">Enjoy ad-free streaming across all your devices</span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="text-green-400 mt-0.5 flex-shrink-0" fontSize="small" />
              <span className="text-gray-300 text-sm">Download songs for offline listening</span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="text-green-400 mt-0.5 flex-shrink-0" fontSize="small" />
              <span className="text-gray-300 text-sm">Access higher quality audio settings</span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="text-green-400 mt-0.5 flex-shrink-0" fontSize="small" />
              <span className="text-gray-300 text-sm">Unlimited skips and playlist creation</span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <Link
            to="/"
            className="inline-block px-6 py-3 bg-naia-pink hover:bg-pink-500 text-white rounded-xl font-medium transition-colors mr-4"
          >
            Start Listening
          </Link>
          <Link
            to="/subscription"
            className="inline-block px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-medium transition-colors"
          >
            Manage Subscription
          </Link>
        </div>

        <p className="text-gray-400 text-sm mt-6">
          A confirmation email has been sent to your registered email address.
        </p>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
